import React, { useEffect, useRef, useState } from 'react'
import { EKYC_BASE_URL } from "../config/environment";

/**
 * Combined Selfie + Liveness modal.
 *
 * Step 1 (Selfie):
 *  - Open camera with getUserMedia, start recording video in the background.
 *  - User clicks Capture → we grab a still frame (selfie) and stop recording.
 *
 * Step 2 (Liveness):
 *  - We create an Azure liveness session, start the web SDK inside the same modal.
 *  - When the user clicks Done, we return selfie image, recorded video, and sessionId.
 *
 * onCaptured receives: { selfieFile, livenessVideo, livenessSessionId }.
 */
export default function SelfieCaptured({ onCancel, onCaptured }) {
    const selfieVideoRef = useRef(null)
    const mediaRecorderRef = useRef(null)
    const mediaStreamRef = useRef(null)
    const chunksRef = useRef([])
    const selfieBlobRef = useRef(null)
    const livenessVideoBlobRef = useRef(null)
    const azureRef = useRef(null)

    const [phase, setPhase] = useState('selfie') // 'selfie' | 'liveness'
    const [error, setError] = useState('')
    const [ready, setReady] = useState(false)
    const [sessionInfo, setSessionInfo] = useState(null)
    const [creatingSession, setCreatingSession] = useState(false)

    // Step 1: start local camera + background recording as soon as the modal mounts.
    useEffect(() => {
        let cancelled = false
            ; (async () => {
                try {
                    const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false })
                    if (cancelled) {
                        stream.getTracks().forEach(t => t.stop())
                        return
                    }
                    mediaStreamRef.current = stream
                    if (selfieVideoRef.current) {
                        selfieVideoRef.current.srcObject = stream
                        await selfieVideoRef.current.play()
                        setReady(true)
                    }
                    const mr = new MediaRecorder(stream, { mimeType: 'video/webm' })
                    mediaRecorderRef.current = mr
                    chunksRef.current = []
                    mr.ondataavailable = (e) => {
                        if (e.data && e.data.size > 0) chunksRef.current.push(e.data)
                    }
                    mr.onstop = () => {
                        try {
                            if (chunksRef.current.length > 0) {
                                livenessVideoBlobRef.current = new Blob(chunksRef.current, { type: 'video/webm' })
                            }
                        } catch {
                            // ignore; selfie alone is still useful
                        }
                    }
                    mr.start()
                } catch (e) {
                    console.error('Selfie camera error', e)
                    if (!cancelled) setError('Cannot access camera. Please allow camera permission or use file upload.')
                }
            })()
        return () => {
            cancelled = true
            try {
                if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                    mediaRecorderRef.current.stop()
                }
            } catch {
                // ignore
            }
            if (mediaStreamRef.current) {
                mediaStreamRef.current.getTracks().forEach(t => t.stop())
                mediaStreamRef.current = null
            }
        }
    }, [])

    const captureSelfie = () => {
        if (!selfieVideoRef.current) return
        try {
            const video = selfieVideoRef.current
            const canvas = document.createElement('canvas')
            const w = video.videoWidth || 640
            const h = video.videoHeight || 480
            canvas.width = w
            canvas.height = h
            const ctx = canvas.getContext('2d')
            ctx.drawImage(video, 0, 0, w, h)
            canvas.toBlob((blob) => {
                if (!blob) {
                    setError('Failed to capture selfie. Please try again.')
                    return
                }
                selfieBlobRef.current = blob
                // Stop recording and camera; when recording stops, onstop will finalize video blob.
                try {
                    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                        mediaRecorderRef.current.stop()
                    }
                } catch {
                    // ignore
                }
                if (mediaStreamRef.current) {
                    mediaStreamRef.current.getTracks().forEach(t => t.stop())
                    mediaStreamRef.current = null
                }
                // Move to liveness phase and start session.
                beginLiveness()
            }, 'image/png')
        } catch (e) {
            console.error('Capture selfie error', e)
            setError('Failed to capture selfie. Please try again.')
        }
    }

    const beginLiveness = async () => {
        setPhase('liveness')
        setError('')
        setCreatingSession(true)
        try {
            // const base = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'
            const base = EKYC_BASE_URL;

            if (!base) {
                throw new Error("REACT_APP_API_BASE_URL is not defined");
            }
            const res = await fetch(base + '/liveness/session', { method: 'POST' })
            if (!res.ok) throw new Error('Failed to create liveness session')


            const js = await res.json()
            const payload = js?.data || js
            const sid = payload.sessionId || payload.session_id || payload.id
            const token =
                payload.clientToken ||
                payload.authToken ||
                payload.auth_token ||
                payload.token ||
                payload?.auth?.token
            if (!sid || !token) {
                console.error('Unexpected liveness session payload', payload)
                throw new Error('Invalid liveness session response')
            }


            setSessionInfo({ sessionId: sid, clientToken: token })
        } catch (e) {
            console.error('Liveness session error', e)
            setError('Unable to start liveness check. Please close and try again.')
        } finally {
            setCreatingSession(false)
        }
    }

    // Start Azure Face Liveness UI once we have session info.
    useEffect(() => {
        if (phase !== 'liveness' || !sessionInfo) return
        let cancelled = false

        // const startSdkWhenReady = (retries = 20) => {
        //     if (cancelled) return
        //     const el = azureRef.current

        //     if (!el || typeof el.start !== 'function') {
        //         if (retries > 0) {
        //             setTimeout(() => startSdkWhenReady(retries - 1), 100)
        //         } else {
        //             console.error('Azure liveness UI not ready (no start method)')
        //             setError('Unable to start liveness UI. Please close and try again.')
        //         }
        //         return
        //     }
        //     try {
        //         el.start(sessionInfo.clientToken)
        //     } catch (e) {
        //         console.error('Face UI start error', e)
        //         setError('Unable to start liveness UI. Please close and try again.')
        //     }
        // }

        const startSdkWhenReady = async (retries = 20) => {
            if (cancelled) return

            const el = azureRef.current


            if (!el || typeof el.start !== 'function') {
                if (retries > 0) {
                    setTimeout(() => {
                        startSdkWhenReady(retries - 1).catch((err) => {
                            console.error(" Retry failed:", err)
                        })
                    }, 100)
                } else {
                    console.error(' Azure liveness UI not ready (no start method)')
                    setError('Unable to start liveness UI. Please close and try again.')
                }
                return
            }

            try {
               
                const result = await el.start(sessionInfo.clientToken) // ✅ IMPORTANT

                console.log(" Liveness SUCCESS:", result)

            } catch (error) {
                console.error(" Liveness SDK ERROR:", error)
            

                const message = getLivenessErrorMessage(error)
                setError(message)
            }
        }

        startSdkWhenReady()

        return () => {
            cancelled = true
        }
    }, [phase, sessionInfo])

    const getLivenessErrorMessage = (error) => {
        const livenessError = error?.livenessError || ""

        switch (livenessError) {
            case "NoFaceDetected":
                return "Face not detected. Keep your face visible inside the circle and try again."

            case "FaceTrackingLost":
                return "Face tracking was lost. Hold the camera steady and keep your full face visible."

            case "MultipleFacesDetected":
                return "Multiple faces detected. Ensure only one person is in frame."

            default:
                return "Face not detected. Keep your face visible inside the circle and try again."
        }
    }


    const handleDone = () => {
        if (!selfieBlobRef.current || !sessionInfo?.sessionId) {
            setError('Selfie or liveness session missing. Please retry.')
            return
        }
        const selfieFile = new File([selfieBlobRef.current], 'selfie.png', { type: 'image/png' })
        const videoBlob = livenessVideoBlobRef.current
        const videoFile = videoBlob
            ? new File([videoBlob], 'selfie_liveness.webm', { type: 'video/webm' })
            : null
        onCaptured({
            selfieFile,
            livenessVideo: videoFile,
            livenessSessionId: sessionInfo.sessionId,
        })
    }

    const handleCancel = () => {
        try {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                mediaRecorderRef.current.stop()
            }
        } catch {
            // ignore
        }
        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(t => t.stop())
            mediaStreamRef.current = null
        }
        onCancel()
    }

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: '#fff', borderRadius: 12, maxWidth: 680, width: '95%', padding: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h3 style={{ marginTop: 0 }}>Selfie & Liveness</h3>
                    <button className="btn" onClick={handleCancel} aria-label="Close">×</button>
                </div>
                {error && <div className="alert" style={{ marginBottom: 12 }}>{error}</div>}
                {phase === 'selfie' && (
                    <>
                        <p className="muted" style={{ marginBottom: 8 }}>
                            Step 1 of 2 — Look at the camera and click Capture to take your selfie. We record a short video while the camera is open.
                        </p>
                        <video
                            ref={selfieVideoRef}
                            autoPlay
                            playsInline
                            style={{ width: '100%', borderRadius: 12, background: '#000', border: '1px solid #e5e7eb' }}
                        />
                        <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button className="btn" onClick={handleCancel}>Cancel</button>
                            <button className="btn btn-primary" onClick={captureSelfie} disabled={!ready}>Capture</button>
                        </div>
                    </>
                )}
                {phase === 'liveness' && (
                    <>
                        <p className="muted" style={{ marginBottom: 8 }}>
                            Step 2 of 2 — Liveness check. Follow the on‑screen guidance in the widget below, then click Done.
                        </p>
                        {creatingSession && <div className="muted" style={{ marginBottom: 8 }}>Starting liveness session…</div>}
                        <azure-ai-vision-face-ui
                            ref={azureRef}
                            style={{ display: 'block', width: '100%', maxWidth: 420, margin: '0 auto 8px' }}
                        ></azure-ai-vision-face-ui>
                        <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button className="btn" onClick={handleCancel}>Cancel</button>
                            <button className="btn btn-primary" onClick={handleDone} disabled={!sessionInfo || !!error}>
                                Done
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}




